import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HelpCircle, ChevronDown, ChevronUp, CreditCard, Calendar, Tag,
  ArrowUpCircle, Megaphone, Star
} from 'lucide-react';
import usePricingConfig from '../../hooks/usePricingConfig';

export const BusinessGuideSection = ({ business, handleOpenPortal, handleUpgrade, portalLoading }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Iter 208 — Pricing copy is now admin-editable from /admin/pricing-config.
  // We fall back to baked-in strings if the config endpoint is unreachable.
  const pricing = usePricingConfig();

  const is6Month = business?.subscriptionPlan === '6months';
  const isActive = business?.paymentConfirmed && business?.subscriptionStatus === 'active';

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="business-guide-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        data-testid="business-guide-toggle"
      >
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-purple-600" />
          {t('businessGuideTitle', 'How It Works — Your Business Guide')}
        </h2>
        {expanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-5 space-y-4 border-t">
          {/* Subscription Info */}
          <div className="pt-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
              <CreditCard className="h-4 w-4 text-purple-500" />
              {t('guideSubscriptions', 'Subscriptions')}
            </h3>
            <ul className="text-sm text-gray-600 space-y-1.5 ml-6 list-disc">
              {pricing.currentMode === 'paid' ? (
                <>
                  <li>{pricing.foundingTitle}</li>
                  <li>{pricing.foundingTagline}</li>
                  <li>{pricing.trialDescription}</li>
                  <li>{t('guideFoundingProfile', 'Full business profile, photos, contact details, events and offers included.')}</li>
                  <li>{t('guideFoundingLive', 'Your listing goes live once approved.')}</li>
                </>
              ) : (
                <>
                  <li>{t('guideFoundingTitle', 'Founding Business Offer — FREE for 12 months.')}</li>
                  <li>{t('guideFoundingClaim', 'Claim your existing business listing or add a new one free of charge.')}</li>
                  <li>{t('guideFoundingNoCard', 'No card required.')}</li>
                  <li>{t('guideFoundingProfile', 'Full business profile, photos, contact details, events and offers included.')}</li>
                  <li>{t('guideFoundingLive', 'Your listing goes live once approved.')}</li>
                </>
              )}
            </ul>
          </div>

          {/* Upgrade Prompt for legacy 6-month plans */}
          {is6Month && isActive && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4" data-testid="upgrade-prompt">
              <div className="flex items-start gap-3">
                <ArrowUpCircle className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold text-purple-900">{t('upgradeTitle', 'Move to the 12-Month Plan')}</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    {t('upgradeDesc', "You're currently on a legacy 6-month plan. All businesses are now free for 12 months under the Founding Business Offer — upgrade in one click to roll over with no payment required during the founding period.")}
                  </p>
                  <button
                    onClick={() => handleUpgrade('12months')}
                    disabled={portalLoading}
                    className="mt-3 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                    data-testid="upgrade-btn"
                  >
                    {portalLoading ? t('processing', 'Processing...') : t('upgradeNow', 'Upgrade Now')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Events & Offers Limits */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-purple-500" />
              {t('guideEventsOffers', 'Events & Offers')}
            </h3>
            <ul className="text-sm text-gray-600 space-y-1.5 ml-6 list-disc">
              <li>{t('guideEventLimit', 'You can have up to 5 active events at a time. Archive old events to create new ones.')}</li>
              <li>{t('guideOfferLimit', 'You can have up to 5 active offers at a time. Delete expired offers to add new ones.')}</li>
              <li>{t('guideEventApproval', 'Events and offers go through a quick admin review before they go live.')}</li>
            </ul>
          </div>

          {/* Boosts */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
              <Megaphone className="h-4 w-4 text-purple-500" />
              {t('guideBoosts', 'Boosts & Visibility')}
            </h3>
            <ul className="text-sm text-gray-600 space-y-1.5 ml-6 list-disc">
              <li>{t('guideBoostDesc', 'Boost your events or offers to appear at the top of listings and reach more people.')}</li>
              <li>{t('guideBoostTiers', 'Choose from different boost durations to match your budget and campaign.')}</li>
            </ul>
          </div>

          {/* Reviews */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
              <Star className="h-4 w-4 text-purple-500" />
              {t('guideReviews', 'Reviews')}
            </h3>
            <ul className="text-sm text-gray-600 space-y-1.5 ml-6 list-disc">
              <li>{t('guideReviewsDesc', 'Users can leave reviews on your listing. Good reviews help attract more customers.')}</li>
            </ul>
          </div>

          {/* Billing */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
              <Tag className="h-4 w-4 text-purple-500" />
              {t('guideBilling', 'Billing & Payments')}
            </h3>
            <ul className="text-sm text-gray-600 space-y-1.5 ml-6 list-disc">
              {pricing.currentMode === 'paid' ? (
                <li>{pricing.trialDescription}</li>
              ) : (
                <>
                  <li>{t('guideBillingNoPay', 'No payment is required during the 12-month founding member period.')}</li>
                  <li>{t('guideBillingFuture', 'Future pricing (if introduced) will be communicated well in advance.')}</li>
                </>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
